import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { CalendarDays, MapPin, Ticket, Plus, ArrowRight, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const C = { beige: '#F6F0E6', beigeDeep: '#EFE6D6', ink: '#17120D', gold: '#E9C46A', red: '#D62828' };

function resolveMedia(url) {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${API_URL}${url}`;
}
function fmtDate(v) {
  if (!v) return null;
  try { return format(new Date(v), 'd MMM', { locale: fr }); } catch { return null; }
}

/**
 * Bloc Home « Événements & Billetterie ».
 * S'auto-masque s'il n'y a aucun événement publié (module billetterie OFF ou vide)
 * → aucun bloc vide en prod. Grille responsive (zéro scroll horizontal).
 */
export default function HomeEventsBlock() {
  const navigate = useNavigate();
  const [events, setEvents] = useState(null); // null = en cours, [] = vide

  useEffect(() => {
    let active = true;
    axios.get(`${API_URL}/api/tickets/public/events?limit=4`)
      .then((r) => { if (active) setEvents(Array.isArray(r.data?.events) ? r.data.events : []); })
      .catch(() => { if (active) setEvents([]); });
    return () => { active = false; };
  }, []);

  // Auto-masquage : rien tant qu'on charge, et rien si aucun événement.
  if (!events || events.length === 0) return null;

  return (
    <section className="py-10" data-testid="home-events">
      <div className="container mx-auto px-4">
        <div className="overflow-hidden rounded-3xl" style={{ background: C.ink }}>
          <div className="flex flex-col gap-4 px-5 py-7 sm:px-8">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: C.gold }}>
                  <Sparkles className="h-3.5 w-3.5" /> InfoFlow Events
                </span>
                <h2 className="mt-1 text-2xl font-black sm:text-3xl" style={{ color: C.beige }}>Événements &amp; Billetterie</h2>
              </div>
              <Link to="/events" className="inline-flex items-center gap-1 text-sm font-semibold hover:underline" style={{ color: C.gold }}>
                Voir tout <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {events.map((ev) => {
                const cover = resolveMedia(ev.visuel_url);
                const date = fmtDate(ev.date_debut);
                return (
                  <button key={ev.event_id} onClick={() => navigate(`/event/${ev.event_id}`)}
                          className="group overflow-hidden rounded-xl text-left" style={{ background: 'rgba(246,240,230,0.06)', border: '1px solid rgba(233,196,106,0.18)' }}>
                    <div className="relative aspect-[4/3] w-full overflow-hidden">
                      {cover ? (
                        <img src={cover} alt={ev.titre} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      ) : (
                        <div className="h-full w-full" style={{ background: 'linear-gradient(135deg,#3a2c1e,#17120d)' }} />
                      )}
                    </div>
                    <div className="p-2.5">
                      <p className="line-clamp-2 text-sm font-bold" style={{ color: C.beige }}>{ev.titre}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]" style={{ color: 'rgba(246,240,230,0.65)' }}>
                        {date ? <span className="inline-flex items-center gap-0.5"><CalendarDays className="h-3 w-3" />{date}</span> : null}
                        {ev.lieu ? <span className="inline-flex items-center gap-0.5 truncate"><MapPin className="h-3 w-3" />{ev.lieu}</span> : null}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button onClick={() => navigate('/events')} className="h-11 flex-1 rounded-full font-bold" style={{ background: C.red, color: '#fff' }}>
                <Ticket className="mr-2 h-4 w-4" /> Acheter un billet
              </Button>
              <Button onClick={() => navigate('/billetterie/creer')} variant="outline" className="h-11 flex-1 rounded-full font-semibold" style={{ background: 'transparent', color: C.beige, borderColor: 'rgba(246,240,230,0.4)' }}>
                <Plus className="mr-2 h-4 w-4" /> Créer un événement
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
